if (!customElements.get('drilldown-sidebar')) {
  class DrilldownSidebar extends HTMLElement {
    constructor() {
      super();

      this.overlay = null;
      this.panel = null;
      this.columnsWrapper = null;
      this.closeButton = null;
      this.topMenuTriggers = null;
      this.announcement = null;
      this.lastFocusedElement = null;
      this.focusableElements = null;

      this.handleColumnsClick = this.handleColumnsClick.bind(this);
      this.handleOverlayClick = this.handleOverlayClick.bind(this);
      this.handleCloseClick = this.handleCloseClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleTopMenuClick = this.handleTopMenuClick.bind(this);
      this.handleExternalOpenClick = this.handleExternalOpenClick.bind(this);
    }

    connectedCallback() {
      this.overlay = this.querySelector('[data-drilldown-overlay]');
      this.panel = this.querySelector('.drilldown-sidebar__panel');
      this.columnsWrapper = this.querySelector('[data-drilldown-columns]');
      this.closeButtons = this.querySelectorAll('[data-drilldown-close]');
      this.announcement = this.querySelector('[data-drilldown-announcement]');

      if (!this.columnsWrapper) return;

      // Hide all sub-columns on initialization (levels > 1)
      this.hideAllSubColumns();

      // Events inside sidebar
      this.columnsWrapper.addEventListener('click', this.handleColumnsClick);

      if (this.overlay) {
        this.overlay.addEventListener('click', this.handleOverlayClick);
      }

      if (this.closeButtons) {
        this.closeButtons.forEach((btn) => {
          btn.addEventListener('click', this.handleCloseClick);
        });
      }

      document.addEventListener('keydown', this.handleKeydown);

      // Bind to top menu (triggers outside component)
      this.topMenuTriggers = document.querySelectorAll('[data-sidebar-open-index]');
      this.topMenuTriggers.forEach((trigger) => {
        trigger.addEventListener('click', this.handleTopMenuClick);
      });

      this.externalTriggers = document.querySelectorAll('[data-sidebar-open]');
      this.externalTriggers.forEach((trigger) => {
        trigger.addEventListener('click', this.handleExternalOpenClick);
      });
    }

    disconnectedCallback() {
      if (this.columnsWrapper) {
        this.columnsWrapper.removeEventListener('click', this.handleColumnsClick);
      }
      if (this.overlay) {
        this.overlay.removeEventListener('click', this.handleOverlayClick);
      }
      if (this.closeButton) {
        this.closeButton.removeEventListener('click', this.handleCloseClick);
      }
      document.removeEventListener('keydown', this.handleKeydown);

      if (this.topMenuTriggers) {
        this.topMenuTriggers.forEach((trigger) => {
          trigger.removeEventListener('click', this.handleTopMenuClick);
        });
      }

      if (this.externalTriggers) {
        this.externalTriggers.forEach((trigger) => {
          trigger.removeEventListener('click', this.handleExternalOpenClick);
        });
      }
    }

    // --------- OPEN / CLOSE ---------

    openAtIndex(index) {
      this.open();
      this.hideAllSubColumns();

      const targetId = `sidebar-level-${index}`;
      // Open child column for given root (1 → sidebar-level-1, etc.)
      this.showColumnById(targetId);
    }

    open() {
      // Store currently focused element
      this.lastFocusedElement = document.activeElement;

      // Save current scroll position
      this.scrollPosition = window.scrollY;

      this.classList.add('drilldown-sidebar--is-open');
      this.setAttribute('aria-hidden', 'false');

      // Prevent body scroll and maintain scroll position
      document.body.style.top = `-${this.scrollPosition}px`;
      document.documentElement.classList.add('drilldown-open');

      // Move focus into sidebar
      this.trapFocus();
    }

    close() {
      this.classList.remove('drilldown-sidebar--is-open');
      this.setAttribute('aria-hidden', 'true');

      // Re-enable body scroll and restore scroll position
      document.documentElement.classList.remove('drilldown-open');
      document.body.style.top = '';

      const savedScrollPosition = this.scrollPosition;

      if (savedScrollPosition !== undefined) {
        window.scrollTo(0, savedScrollPosition);
        this.scrollPosition = undefined;
      }

      this.hideAllSubColumns();
      this.clearActivePath();

      // Restore focus to original element without scrolling
      if (this.lastFocusedElement && this.lastFocusedElement.focus) {
        this.lastFocusedElement.focus({ preventScroll: true });
      }
      this.lastFocusedElement = null;
    }

    trapFocus() {
      // Get all focusable elements
      const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
      ];

      this.focusableElements = this.panel.querySelectorAll(
        focusableSelectors.join(',')
      );

      if (this.focusableElements.length > 0) {
        // Move focus to first close button
        const firstCloseButton = this.panel.querySelector('[data-drilldown-close]');
        if (firstCloseButton) {
          firstCloseButton.focus();
        } else {
          this.focusableElements[0].focus();
        }
      }
    }

    hideAllSubColumns() {
      const subColumns = this.querySelectorAll(
        '.drilldown-sidebar__column[data-drilldown-level]:not([data-drilldown-level="1"])'
      );
      subColumns.forEach((col) => {
        col.hidden = true;
        col.style.display = 'none';
      });
    }

    scrollColumnsToRight() {
      if (!this.columnsWrapper) return;
      this.columnsWrapper.scrollLeft = this.columnsWrapper.scrollWidth;
    }

    // --------- RENDERING BY PATH + ACTIVE PATH ---------

    showColumnById(id) {
      const raw = id.replace(/^sidebar-level-/, '');
      const segments = raw.split('-').filter(Boolean);

      // Set active path in individual columns (the tree)
      this.setActivePath(segments);

      // If no segments, clear path and exit
      if (!segments.length) {
        this.clearActivePath();
        this.updateAriaExpanded();
        return;
      }

      const visibleIds = [];
      for (let i = 0; i < segments.length; i++) {
        const prefix = segments.slice(0, i + 1).join('-');
        visibleIds.push('sidebar-level-' + prefix);
      }

      const allColumns = this.querySelectorAll('.drilldown-sidebar__column[data-drilldown-level]');

      allColumns.forEach((col) => {
        const isRoot = col.getAttribute('data-drilldown-level') === '1';
        const colId = col.id;
        const shouldShow = isRoot || visibleIds.includes(colId);

        col.hidden = !shouldShow;
        col.style.display = shouldShow ? '' : 'none';
      });

      // Update aria-expanded
      this.updateAriaExpanded();

      // Announce for screen readers
      const targetColumn = document.getElementById(id);
      if (targetColumn && this.announcement) {
        const title = targetColumn.querySelector('.drilldown-sidebar__parent-title');
        if (title) {
          this.announcement.textContent = title.textContent.trim();
        }
      }

      this.scrollColumnsToRight();
    }

    updateAriaExpanded() {
      // Update all trigger buttons with aria-expanded
      const allTriggers = this.querySelectorAll('[data-drilldown-target]');
      allTriggers.forEach((trigger) => {
        const targetId = trigger.getAttribute('data-drilldown-target');
        const targetColumn = document.getElementById(targetId);
        const isExpanded = targetColumn && !targetColumn.hidden;
        trigger.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      });
    }

    // --------- ACTIVE STATES BY FULL PATH ---------

    setActivePath(segments) {
      // Clear previous active items
      this.clearActivePath();

      if (!segments || !segments.length) return;

      // For each prefix (1, 1-2, 1-2-3, ...) find trigger
      for (let i = 0; i < segments.length; i++) {
        const prefix = segments.slice(0, i + 1).join('-');
        const targetId = 'sidebar-level-' + prefix;

        const trigger = this.querySelector('[data-drilldown-target="' + targetId + '"]');

        if (trigger) {
          const item = trigger.closest('.drilldown-sidebar__item');
          if (item) {
            item.classList.add('drilldown-sidebar__item--active');
          }
        }
      }
    }

    clearActivePath() {
      this.querySelectorAll('.drilldown-sidebar__item--active').forEach((item) => {
        item.classList.remove('drilldown-sidebar__item--active');
      });
    }

    // --------- EVENT HANDLERS ---------

    handleColumnsClick(event) {
      const targetButton = event.target.closest('[data-drilldown-target]');
      const backButton = event.target.closest('[data-drilldown-back]');

      if (targetButton) {
        const targetId = targetButton.getAttribute('data-drilldown-target');
        if (targetId) {
          this.showColumnById(targetId);
        }
      }

      if (backButton) {
        const currentColumn = backButton.closest('.drilldown-sidebar__column');
        if (currentColumn && currentColumn.id) {
          const raw = currentColumn.id.replace(/^sidebar-level-/, '');
          const segments = raw.split('-').filter(Boolean);

          if (segments.length > 1) {
            // Go back one level: 1-2-3 -> 1-2
            const parentPath = segments.slice(0, -1).join('-');
            const parentId = 'sidebar-level-' + parentPath;
            this.showColumnById(parentId);
          } else {
            // We're in first child level → hide all >1 and clear active
            this.hideAllSubColumns();
            this.clearActivePath();
          }
        }
      }
    }

    handleOverlayClick() {
      this.close();
    }

    handleCloseClick() {
      this.close();
    }

    handleKeydown(event) {
      if (!this.classList.contains('drilldown-sidebar--is-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      // Focus trap - Tab key
      if (event.key === 'Tab') {
        if (!this.focusableElements || this.focusableElements.length === 0) {
          event.preventDefault();
          return;
        }

        const firstElement = this.focusableElements[0];
        const lastElement = this.focusableElements[this.focusableElements.length - 1];

        if (event.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    }

    handleTopMenuClick(event) {
      const trigger = event.currentTarget;
      const index = trigger.getAttribute('data-sidebar-open-index');

      if (!index) return;

      // If link has children, open sidebar instead of navigating
      event.preventDefault();
      this.openAtIndex(index);
    }

    handleExternalOpenClick(event) {
      event.preventDefault();
      this.open(); // Just open panel
      this.hideAllSubColumns(); // Hide levels > 1
      this.clearActivePath(); // Clear active states
    }
  }

  customElements.define('drilldown-sidebar', DrilldownSidebar);
}
